<!--
  File name: LookUpSelect.vue
  Program Description:
    -  This component uses https://www.codeply.com/p/BOwzZKhFvY and
    the LookUpSelect of maroon-vue-components
    - This component allows entries in the dropdown field to load the items
    depending on the count set to lessen initial loading times for dropdown
    fields with multiple items

-->
<template>
  <v-autocomplete
    v-model="$data.selectedText"
    autocomplete="off"
    :dense="isDense"
    :disabled="!canEdit || disabled"
    :items="addedEntries"
    :item-text="itemText"
    item-value="id"
    :label="label"
    :loading="loading"
    :clearable="clearable"
    return-object
    :error-messages="errorMessages"
    :append-icon="appendIcon"
    :solo="solo"
    :flat="flat"
    :chips="chips"
    :small-chips="smallChips"
    :can-edit="canEdit"
    :outlined="outlined"
    :readonly="readonly"
    :multiple="multiple"
    @change="onChange($event)"
  >
    <template
      v-if="chips || (textWrap && !multiple)"
      #selection="{ item }"
    >
      <v-chip
        v-if="chips"
        :color="chipColor"
        :small="smallChips"
        :text-color="chipTextColor"
      >
        {{ item[`${itemText}`] }}
      </v-chip>
      <span
        v-else-if="textWrap"
        :style="{
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          hyphens: 'auto',
          color: 'black'
        }"
      >
        {{ item[`${itemText}`] }}
      </span>
    </template>
    <template v-slot:append-item>
      <div
        v-intersect="onIntersect"
        :style="{ pointerEvents: 'none' }"
      />
    </template>
    <template
      v-if="tooltip"
      slot="append"
    >
      <v-tooltip
        right
        max-width="400"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            color="gray"
            depressed
            v-bind="attrs"
            style="cursor: default"
            v-on="on"
          >
            mdi-information
          </v-icon>
        </template>
        <span>{{ tooltipInfo }}</span>
      </v-tooltip>
      <v-icon>mdi-menu-down</v-icon>
    </template>
  </v-autocomplete>
</template>

<script>
/* eslint-disable vue/prop-name-casing */
export default {
  props: {
    id: {
      type: String,
      default: undefined
    },
    action: {
      type: String,
      default: 'getOptions'
    },
    clearable: {
      type: Boolean,
      default: true
    },
    tooltip: {
      type: Boolean,
      default: false
    },
    'tooltip-info': {
      type: String,
      default: 'info'
    },
    'item-text': {
      type: String,
      default: 'name'
    },
    'item-value': {
      type: String,
      default: '_id'
    },
    'return-object': {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    isUser: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: undefined
    },
    query: {
      type: Object,
      default: undefined
    },
    value: {
      type: [String, Object, Array],
      default: undefined
    },
    outlined: Boolean,
    solo: Boolean,
    flat: Boolean,
    canEdit: Boolean,
    'error-messages': {
      type: Array,
      default: undefined
    },
    'text-field-class': {
      type: String,
      default: undefined
    },
    isDense: {
      type: Boolean,
      default: true
    },
    nestedFrom: {
      type: String,
      default: undefined
    },
    unique: Boolean,
    view: {
      type: String,
      default: undefined
    },
    crudCollection: {
      type: Boolean,
      default: false
    },
    'look-up-select-style': {
      type: Object,
      default: undefined
    },
    extQuery: {
      type: String,
      default: undefined
    },
    allowFetch: {
      type: [String, Boolean, Array],
      default: undefined
    },
    additionalEntries: {
      type: Array,
      default: () => []
    },
    appendIcon: {
      type: String,
      default: '$dropdown'
    },
    chips: {
      type: Boolean,
      default: false
    },
    smallChips: {
      type: Boolean,
      default: false
    },
    chipColor: {
      type: String,
      default: undefined
    },
    chipTextColor: {
      type: String,
      default: undefined
    },
    multiple: {
      type: Boolean,
      default: false
    },
    textWrap: {
      type: Boolean,
      default: false
    }

  },
  data: () => ({
    totalFetched: 0,
    selectedText: '',
    entries: [],
    loading: true,
    disabled: true,
    currentQuery: {},
    filter: {
      search: '',
      page: 1
    },
    menu: false
  }),
  computed: {
    identifier () {
      return this.id || this.name
    },
    addedEntries () {
      const array = this.additionalEntries !== undefined ? [...this.entries, ...this.additionalEntries] : this.entries
      return array
    }
  },
  watch: {
    query (value, prev) {
      if (this.allowFetch) {
        if (!this.deepEqual(value, this.currentQuery) || !(JSON.stringify(value) === JSON.stringify(prev))) {
          !this.value && (this.$data.selectedText = '')
          this.$data.filter.search = ''
          this.$data.filter.page = 1
          this.fetch()
        }
      }
    },
    value () {
      this.setText()
    },
    selectedText (val) {
      !val && this.$emit('input', null)
    },
    'filter.page' () {
      this.fetch(false)
    }
  },
  async created () {
    await this.fetch()
    this.setText()
  },
  methods: {
    returnItem (item) {
      return this.returnObject ? item : (item[this.itemValue] || item)
    },
    removeDuplicates (originalArray, prop) {
      return originalArray.filter((el, index, self) => self.findIndex(t => t[`${prop}`] === el[`${prop}`]) === index)
    },
    formQuery ({ searchText, page, itemsPerPage, additionalQuery = '' }) {
      const start = (page - 1) * itemsPerPage
      const advancedQuery = this.advancedQuery({ searchText, additionalQuery })
      return `?count=${itemsPerPage}&start=${start}${advancedQuery}`
    },
    formColumnQuery (searchText) {
      if (this.nestedFrom) return [{ [`${this.name}.${this.itemText}`]: { $regex: searchText, $options: 'i' } }]
      return [{ [this.itemText]: { $regex: searchText, $options: 'i' } }]
    },
    advancedQuery ({ searchText, additionalQuery }) {
      const qObj = { $and: [] }
      const userList = ['professor', 'auh', 'cid', 'userRole', 'registrar', 'student']
      const typeOfQuery = userList.includes(this.name) || this.isUser ? 'advancedQuery' : 'queries'
      searchText && qObj.$and.push({ $or: this.formColumnQuery(searchText) })
      if (additionalQuery !== '') { qObj.$and.push(additionalQuery) }
      if (!qObj.$and.length) delete qObj.$and
      return `&${typeOfQuery}=${encodeURI(JSON.stringify(qObj))}`
    },
    async fetch (resetItems = true) {
      this.loading = true
      this.disabled = true
      const aQuery = this.query
      if (aQuery && aQuery.type === 'city') aQuery.type = 'town'
      aQuery && Object.keys(aQuery).forEach(s => (!aQuery[`${s}`] && aQuery[`${s}`] !== '') && delete aQuery[`${s}`])

      const { page, search } = this.$data.filter
      const req = {
        page,
        itemsPerPage: 10,
        additionalQuery: aQuery
      }
      search && (req.searchText = search)
      this.view && (req.view = this.view)

      const query = this.formQuery(req)
      const resp = await this.$store.dispatch(`lookups/${this.action}`, {
        type: this.name,
        query,
        nested: this.nestedFrom,
        resetItems,
        req,
        useCrud: this.crudCollection,
        extQuery: this.extQuery
      })

      // combine (check if resetItems=true) then reduce nested
      let combinedEntries
      if (resetItems === true) {
        combinedEntries = resp ? resp.entries : []
      } else {
        combinedEntries = this.entries.concat(resp.entries)
      }
      combinedEntries = this.reduceNested(combinedEntries, this.name, this.nestedFrom)
      this.entries = this.unique ? this.removeDuplicates(combinedEntries, this.itemValue) : combinedEntries
      this.totalFetched = resp ? resp.totalFetched : 0
      this.value && this.setText()

      this.loading = false
      this.disabled = false
      this.currentQuery = this.$props.query
    },
    text (item) {
      if (this.itemText === 'fullName' && !item.fullName) return `${item.firstName} ${item.lastName}`
      return item[this.itemText] || item
    },
    scroll (event) {
      const e = event.target
      if (Math.ceil(e.scrollTop) + e.clientHeight >= e.scrollHeight && !this.loading) {
        this.handlePage()
      }
    },
    handlePage () {
      const filter = this.$data.filter
      if (this.totalFetched > (filter.page * 10)) {
        filter.page++
      }
    },
    setText () {
      if (this.value) {
        const item = this.addedEntries.find(item => (item[this.itemValue] || item) === (this.value[this.itemValue] || this.value))
        item && (this.$data.selectedText = this.text(item))
        !item && this.handlePage()
      } else {
        this.$data.selectedText = ''
      }
    },
    searchItems (e) {
      this.$data.filter.page = 1
      this.$data.filter.search = e
      clearTimeout(this.timeout)
      this.timeout = setTimeout(async () => {
        await this.fetch()
        this.$refs[this.identifier].focus()
      }, 300)
    },
    reduceNested (data, type, nested) {
      if (!nested) return data
      return data.reduce((curr, key) => {
        if (Array.isArray(key[`${type}`])) key[`${type}`].length && curr.push(...key[`${type}`])
        else curr.push(key[`${type}`])
        return curr
      }, [])
    },
    deepEqual (object1, object2) {
      const keys1 = Object.keys(object1)
      const keys2 = Object.keys(object2)

      if (keys1.length !== keys2.length) {
        return false
      }

      for (const key of keys1) {
        const val1 = object1[`${key}`]
        const val2 = object2[`${key}`]
        const areObjects = this.isObject(val1) && this.isObject(val2)
        if (
          (areObjects && !this.deepEqual(val1, val2)) ||
          (!areObjects && (val1 !== val2))
        ) {
          return false
        }

        if (val1 === '' && val2 === '') {
          return false
        }
      }

      return true
    },
    isObject (object) {
      return object != null && typeof object === 'object'
    },
    onChange (item) {
      const emitValue = item ? this.returnItem(item) : ''
      this.$emit('input', emitValue)
    },
    onIntersect () {
      this.handlePage()
    }
  }
}
</script>
